import { CommunicationHub } from "@/components/communication/CommunicationHub";
import { RoutingPolicyEditor } from "@/components/communication/RoutingPolicyEditor";
import { DeliveryHistory } from "@/components/communication/DeliveryHistory";
import { DiscordConfig } from "./DiscordConfig";
import { TelegramConfig } from "./TelegramConfig";
import { EmailConfig } from "./EmailConfig";
import { SlackConfig } from "./SlackConfig";
import { TeamsConfig } from "./TeamsConfig";
import { JiraConfig } from "./JiraConfig";
import { Skeleton } from "@/components/ui/skeleton";
import { useNotificationSettings, useUpdateNotificationSettings } from "@/hooks/useNotificationSettings";
import { useProfile } from "@/hooks/useProfile";
import { toast } from "sonner";

/**
 * CommunicationSettingsPage
 *
 * The top-level settings page for the Communication Engine.
 * Organises the four Atlas-aligned sections:
 *   1. Connections    — connected channels + health status
 *   2. Policies       — routing policy matrix (event class × connection)
 *   3. Health         — needs attention + delivery rate
 *   4. History        — recent delivery facts
 *
 * Contains CommunicationHub as its primary component.
 * Individual channel config panels (DiscordConfig, TeamsConfig, etc.)
 * are rendered below for credential management.
 */
export function CommunicationSettingsPage() {
  const { data: settings, isLoading } = useNotificationSettings();
  const updateSettings = useUpdateNotificationSettings();
  const { data: profile } = useProfile();
  const isPro = profile?.is_pro || false;

  const handleSave = async (updates: Record<string, unknown>) => {
    try { await updateSettings.mutateAsync(updates); }
    catch { toast.error("Failed to save settings"); }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-48 rounded-none" />
        <Skeleton className="h-48 rounded-none" />
      </div>
    );
  }

  const s = settings || {} as any;

  // Derive connection summaries from notification_settings for display
  // (In production this will come from the `connections` table via API)
  const connections = [
    s.slack_webhook_url && { id: "slack", type: "slack", label: "Slack", health_status: s.slack_enabled ? "connected" : "verifying", last_delivery_at: null, last_error: null },
    s.discord_webhook_url && { id: "discord", type: "discord", label: "Discord", health_status: s.discord_enabled ? "connected" : "verifying", last_delivery_at: null, last_error: null },
    s.telegram_bot_token && { id: "telegram", type: "telegram", label: "Telegram", health_status: s.telegram_enabled ? "connected" : "verifying", last_delivery_at: null, last_error: null },
    s.teams_webhook_url && { id: "teams", type: "teams", label: "Microsoft Teams", health_status: s.teams_enabled ? "connected" : "verifying", last_delivery_at: null, last_error: null },
    s.jira_url && { id: "jira", type: "jira", label: "Jira", health_status: s.jira_enabled ? "connected" : "verifying", last_delivery_at: null, last_error: null },
    s.email_enabled && { id: "email", type: "email", label: "Email", health_status: "connected" as const, last_delivery_at: null, last_error: null },
  ].filter(Boolean) as any[];

  return (
    <div className="space-y-10">

      {/* Page Header */}
      <div className="border-4 border-foreground p-8 bg-background shadow-brutal">
        <div className="mb-2">
          <h2 className="text-3xl font-black uppercase tracking-tighter">Communication</h2>
          <p className="text-[10px] font-bold uppercase tracking-widest text-muted-foreground mt-1">
            Operational communication engine — connected channels, routing policies, and delivery history.
          </p>
        </div>
      </div>

      {/* Section divider utility */}
      {(() => {
        const Divider = ({ label }: { label: string }) => (
          <div className="flex items-center gap-4">
            <div className="h-px flex-1 bg-foreground/10" />
            <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">{label}</h2>
            <div className="h-px flex-1 bg-foreground/10" />
          </div>
        );
        return null; // Render inline below
      })()}

      {/* ── Section 1: Connections ── */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-foreground/10" />
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Connections</h2>
          <div className="h-px flex-1 bg-foreground/10" />
        </div>
        <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
          <CommunicationHub
            connections={connections}
            onReconnect={(id) => toast.info(`Reconnect flow for ${id} — configure below`)}
            onAddConnection={() => toast.info("Scroll down to configure a new channel")}
          />
        </div>
      </div>

      {/* ── Section 2: Policies ── */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-foreground/10" />
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Routing Policies</h2>
          <div className="h-px flex-1 bg-foreground/10" />
        </div>
        <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
          <p className="text-xs font-black uppercase tracking-tight mb-1">Default Workspace Policy</p>
          <p className="text-[9px] text-muted-foreground uppercase font-bold tracking-widest mb-5">
            Control which channels receive which operational events.
          </p>
          <RoutingPolicyEditor
            connections={connections}
            rules={[]}
            onSave={async (rules) => {
              toast.success("Routing policy saved");
              console.log("[RoutingPolicyEditor] Rules saved:", rules);
            }}
          />
        </div>
      </div>

      {/* ── Section 3: Channel Configuration ── */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-foreground/10" />
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Channel Configuration</h2>
          <div className="h-px flex-1 bg-foreground/10" />
        </div>
        <div className="space-y-6">
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <SlackConfig settings={s} onSave={handleSave} />
          </div>
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <DiscordConfig settings={s} onSave={handleSave} />
          </div>
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <TelegramConfig settings={s} onSave={handleSave} />
          </div>
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <TeamsConfig settings={s as any} onSave={handleSave} />
          </div>
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <JiraConfig settings={s as any} onSave={handleSave} />
          </div>
          <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
            <EmailConfig settings={s} onSave={handleSave} isPro={isPro} />
          </div>
        </div>
      </div>

      {/* ── Section 4: Delivery History ── */}
      <div className="space-y-4">
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-foreground/10" />
          <h2 className="text-[11px] font-black uppercase tracking-widest text-muted-foreground">Delivery History</h2>
          <div className="h-px flex-1 bg-foreground/10" />
        </div>
        <div className="border-4 border-foreground p-6 bg-background shadow-brutal-sm">
          <p className="text-xs font-black uppercase tracking-tight mb-1">Recent Deliveries</p>
          <p className="text-[9px] text-muted-foreground uppercase font-bold tracking-widest mb-5">
            Immutable delivery facts from the event bus. Every outbound message is recorded here.
          </p>
          <DeliveryHistory records={[]} />
        </div>
      </div>

    </div>
  );
}
